/**
 * Partial date precision model.
 *
 * Tiered date precision (`full | month | year`) for fields where the user may
 * not know the exact day or month — typically date of birth and date of death
 * for distant relatives.
 *
 * Wire format (backend):
 *   - `dob` / `dod` stay as full ISO `YYYY-MM-DD` strings, with missing
 *     components filled by `01` (year-only → `"1980-01-01"`,
 *     month → `"1980-06-01"`).  This preserves byte compatibility with the
 *     XML export pipeline (`generateUserXML` → `parseDate`) consumed by the
 *     desktop importer.
 *   - `estimatedDob` / `estimatedDod` booleans remain truthy whenever
 *     precision is anything other than `full`.
 *   - `dobPrecision` / `dodPrecision` are the new source of truth — string
 *     enum `'full' | 'month' | 'year'`.  Older records with this field
 *     undefined fall back to the legacy boolean.
 *
 * Frontend representation:
 *   `PartialDate` carries year/month/day plus precision so display sites can
 *   render `46`, `~46`, or `45–46` instead of falsely-precise integers.
 *
 * @typedef {'full' | 'month' | 'year'} DatePrecision
 *
 * @typedef {Object} PartialDate
 * @property {number}      year        4-digit year.
 * @property {number|null} month       1–12 when precision is `full` or `month`, else null.
 * @property {number|null} day         1–31 when precision is `full`, else null.
 * @property {DatePrecision} precision Least-precise component the user knows.
 */

export const PRECISION = Object.freeze({
  FULL: 'full',
  MONTH: 'month',
  YEAR: 'year'
});

/**
 * Parse an ISO `YYYY-MM-DD` (or shorter) string into year/month/day numbers.
 * Used internally — does not infer precision.
 *
 * @param {string|null|undefined} iso
 * @returns {{ year: number, month: number|null, day: number|null }|null}
 */
export const parsePartialDateString = (iso) => {
  if (!iso) return null;
  const str = String(iso).trim();
  if (!str) return null;
  // Accept "YYYY", "YYYY-MM", "YYYY-MM-DD"
  const match = /^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/.exec(str);
  if (!match) {
    // Fallback: try Date parse (handles ISO with time component)
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return null;
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate()
    };
  }
  const year = parseInt(match[1], 10);
  const month = match[2] ? parseInt(match[2], 10) : null;
  const day = match[3] ? parseInt(match[3], 10) : null;
  return { year, month, day };
};

/**
 * Build a `PartialDate` from the backend record's date triple.
 *
 *   { iso: "1980-06-15", estimatedFlag: false, precisionField: undefined }
 *     → { year: 1980, month: 6, day: 15, precision: 'full' }
 *
 *   { iso: "1980-01-01", estimatedFlag: true, precisionField: undefined }
 *     → { year: 1980, month: null, day: null, precision: 'year' }   // legacy
 *
 *   { iso: "1980-06-01", estimatedFlag: true, precisionField: 'month' }
 *     → { year: 1980, month: 6, day: null, precision: 'month' }
 *
 * Trusts `precisionField` when present; otherwise falls back to the legacy
 * boolean (true → year precision; false → full precision).
 *
 * @param {Object} args
 * @param {string|null|undefined} args.iso
 * @param {boolean|undefined}     args.estimatedFlag
 * @param {DatePrecision|undefined} args.precisionField
 * @returns {PartialDate|null}
 */
export const partialDateFromBackend = ({
  iso,
  estimatedFlag,
  precisionField
} = {}) => {
  const parts = parsePartialDateString(iso);
  if (!parts) return null;

  const precision =
    precisionField === PRECISION.YEAR ||
    precisionField === PRECISION.MONTH ||
    precisionField === PRECISION.FULL
      ? precisionField
      : estimatedFlag
      ? PRECISION.YEAR
      : PRECISION.FULL;

  if (precision === PRECISION.YEAR) {
    return { year: parts.year, month: null, day: null, precision };
  }
  if (precision === PRECISION.MONTH) {
    return {
      year: parts.year,
      month: parts.month ?? null,
      day: null,
      precision
    };
  }
  return {
    year: parts.year,
    month: parts.month ?? null,
    day: parts.day ?? null,
    precision: PRECISION.FULL
  };
};

/**
 * Serialize a `PartialDate` to the backend's three-field representation.
 *
 *   partialDateToBackend({ year:1980, month:null, day:null, precision:'year' })
 *     → { dob: "1980-01-01", estimatedDob: true, dobPrecision: 'year' }
 *
 *   partialDateToBackend({ year:1980, month:6, day:15, precision:'full' })
 *     → { dob: "1980-06-15", estimatedDob: false, dobPrecision: 'full' }
 *
 * Uses `01` padding for missing month/day so the legacy `dob`/`dod` String
 * field stays a full ISO date — required for XML-export compatibility.
 *
 * @param {PartialDate|null} partial
 * @param {{ date: string, estimated: string, precision: string }} fieldNames
 *   e.g. `{ date: 'dob', estimated: 'estimatedDob', precision: 'dobPrecision' }`
 * @returns {Object} A patch object with the three fields. Date is null when
 *   `partial` is null/undefined; estimated and precision still carry sane
 *   defaults so the record clears cleanly.
 */
export const partialDateToBackend = (partial, fieldNames) => {
  const { date, estimated, precision } = fieldNames;
  if (!partial) {
    // Empty string (not null) — `dob`/`dod` are QE-encrypted strings on the
    // backend and CSFLE refuses to encrypt null (Location31041).
    return {
      [date]: '',
      [estimated]: false,
      [precision]: PRECISION.FULL
    };
  }
  const yearStr = String(partial.year).padStart(4, '0');
  const monthStr = String(partial.month ?? 1).padStart(2, '0');
  const dayStr = String(partial.day ?? 1).padStart(2, '0');
  return {
    [date]: `${yearStr}-${monthStr}-${dayStr}`,
    [estimated]: partial.precision !== PRECISION.FULL,
    [precision]: partial.precision
  };
};

/**
 * Format a `PartialDate` for human display.
 *   full  → "15/06/1980"
 *   month → "06/1980"
 *   year  → "1980"
 *
 * @param {PartialDate|null|undefined} partial
 * @returns {string}
 */
export const formatPartialDate = (partial) => {
  if (!partial) return '';
  const yearStr = String(partial.year);
  if (partial.precision === PRECISION.YEAR) {
    return yearStr;
  }
  const monthStr = String(partial.month ?? 1).padStart(2, '0');
  if (partial.precision === PRECISION.MONTH) {
    return `${monthStr}/${yearStr}`;
  }
  const dayStr = String(partial.day ?? 1).padStart(2, '0');
  return `${dayStr}/${monthStr}/${yearStr}`;
};
